import { Button as ECButton, Popover } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ReactComponent as IconMinus } from '../../assets/icons/minus.svg';
import { ReactComponent as IconMinusBig } from '../../assets/icons/minus_big.svg';
import { ReactComponent as IconPlus } from '../../assets/icons/plus.svg';
import { ReactComponent as IconPlusBig } from '../../assets/icons/plus_big.svg';
import { trackEvent } from '../../utils/analytics';
import { isNil, pluralise, range } from '../../utils/helpers';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import useStyles from './GuestSelectStyles';

const GuestSelect = ({
  value,
  onIncrease,
  onDecrease,
  onSetValue,
  min,
  max,
  allowMaxPlusOne,
  allowedSizes,
  variant,
  unit = 'guest',
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const [viewAllAnchorEl, setViewAllAnchorEl] = useState(null);

  const minGroupCap = Math.max(0, min);
  const maxGroupCap = allowMaxPlusOne ? max + 1 : max;

  const hideIncrementDecrement = allowedSizes?.length > 0;
  const guestRange = hideIncrementDecrement ? allowedSizes : range(minGroupCap, maxGroupCap);
  const isOverMaxGuests = value > max;

  const overRideAllowMaxPlusOne = hideIncrementDecrement ? false : allowMaxPlusOne;

  return (
    <Spacer
      centered
      gap={variant === 'big' ? 0 : 4}
      style={{
        display: 'flex',
        border: variant === 'big' ? `1px solid ${theme.colors.outlineColour}` : 'inherit',
        borderRadius: '12px',
        justifyContent: hideIncrementDecrement ? 'center' : 'space-between',
      }}
    >
      {!hideIncrementDecrement &&
        (variant === 'big' ? (
          <ECButton
            type='custom'
            disabled={value <= minGroupCap}
            variant='outlined'
            onClick={onDecrease}
            style={{
              height: '100%',
              button: {
                width: '40px',
                height: '38px',
                border: 'none',
                borderRadius: '12px 0 0 12px',
              },
              buttonHover: {
                // border: 'none',
                backgroundColor: '#F3F3F3',
                filter: 'none',
              },
              disabled: {
                opacity: '0.2',
              },
            }}
          >
            <IconMinusBig width={12} />
          </ECButton>
        ) : (
          <IconButton disabled={value <= minGroupCap} variant='outlined' onClick={onDecrease}>
            <IconMinus />
          </IconButton>
        ))}

      <Button
        variant='text'
        active={!isNil(viewAllAnchorEl)}
        style={{
          fontWeight: theme.fontWeights.regular,
          ...(variant === 'big' && {
            borderRadius: 0,
            height: '38px',
            // width: '60px',
            fontSize: '18px',
            '&:hover': {
              backgroundColor: '#F3F3F3',
            },
          }),
          ...(hideIncrementDecrement && {
            outline: '1px solid black',
          }),
        }}
        onClick={(e) => {
          trackEvent('Guests', 'Viewed All guest sizes');
          setViewAllAnchorEl(e.currentTarget);
        }}
        showArrowDown={hideIncrementDecrement}
      >
        <Typography className={classes.guests} centered>
          {`${value}${isOverMaxGuests ? '+' : ''} ${pluralise(unit, value)}`}
        </Typography>
      </Button>

      {!hideIncrementDecrement &&
        (variant === 'big' ? (
          <ECButton
            type='custom'
            disabled={value >= maxGroupCap}
            variant='custom'
            onClick={onIncrease}
            style={{
              height: '100%',
              button: {
                width: '40px',
                height: '38px',
                border: 'none',
                borderRadius: '0 12px 12px 0',
              },
              buttonHover: {
                // border: 'none',
                backgroundColor: '#F3F3F3',
                filter: 'none',
              },
              disabled: {
                opacity: '0.2',
              },
            }}
          >
            <IconPlusBig width={12} />
          </ECButton>
        ) : (
          <IconButton disabled={value >= maxGroupCap} variant='outlined' onClick={onIncrease}>
            <IconPlus />
          </IconButton>
        ))}

      <Popover
        anchorEl={viewAllAnchorEl}
        isOpen={!!viewAllAnchorEl}
        onClose={() => setViewAllAnchorEl(null)}
        showArrow
        innerStyle={{
          borderRadius: '8px',
          padding: '0',
          position: 'relative',
          marginTop: '16px',
          // maxWidth: restaurant.data.premium ? !breakpointXsDown && '540px' : 'fit-content',
          width: 'fit-content',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Spacer
          gap={0}
          direction='vertical'
          style={{
            fontFamily: theme.baseTypography.availabilityFontFamily,
            padding: '8px',
            maxHeight: '360px',
          }}
        >
          {guestRange.map((size, index) => (
            <Button
              variant='text'
              key={`guest-${size}`}
              onClick={() => {
                onSetValue(size);
                setViewAllAnchorEl(null);
              }}
              style={{ fontWeight: theme.fontWeights.regular, color: theme.colors.charcoal }}
            >
              <Typography>
                {size}
                {overRideAllowMaxPlusOne && index === guestRange.length - 1 ? '+' : ''}
              </Typography>
            </Button>
          ))}
        </Spacer>
      </Popover>
    </Spacer>
  );
};

GuestSelect.defaultProps = {
  value: null,
  min: 0,
  max: null,
  allowMaxPlusOne: false,
  variant: null,
  unit: 'guest',
  allowedSizes: [],
};

GuestSelect.propTypes = {
  value: PropTypes.number,
  onIncrease: PropTypes.func.isRequired,
  onDecrease: PropTypes.func.isRequired,
  onSetValue: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  allowMaxPlusOne: PropTypes.bool,
  variant: PropTypes.string,
  unit: PropTypes.string,
  allowedSizes: PropTypes.arrayOf(PropTypes.number),
};

export default GuestSelect;
